<template>
  <b-sidebar
    id="create-vehicle-sidebar"
    v-model="showSidebar"
    :bg-variant="isDarkSkin ? 'dark' : 'white'"
    sidebar-class="sidebar-xl"
    shadow
    backdrop
    right
    :title="vehicleId == null ? $t('Create Vehicle') : $t('Edit Vehicle')"
    header-class="text-primary"
    @hidden="closeSidebar(false)"
  >
    <template #default>
      <ValidationObserver ref="vehicle-form">
        <b-form class="p-2">
          <!-- Vehicle Information -->
          <template>
            <b-card>
              <template #header>
                <b-card-title class="form-title">{{
                  $t("Vehicle Information")
                }}</b-card-title>
              </template>
              <template #default>
                <b-row>
                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="setting_vehicle_id"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('Setting') | capitalize"
                        label-for="setting_vehicle_id"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="vehicle.setting_vehicle_id"
                          :options="settings"
                          id="setting_vehicle_id"
                          :reduce="(val) => val.id"
                          label="name"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="vehicle_type_id"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('Type') | capitalize"
                        label-for="vehicle_type_id"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="vehicle.vehicle_type_id"
                          :options="types"
                          id="vehicle_type_id"
                          :reduce="(val) => val.id"
                          label="name"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="vehicle_brand_id"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('Brand') | capitalize"
                        label-for="vehicle_brand_id"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="vehicle.vehicle_brand_id"
                          :options="brands"
                          id="vehicle_brand_id"
                          :reduce="(val) => val.id"
                          label="name"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="vehicle_model_id"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('Model') | capitalize"
                        label-for="vehicle_model_id"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="vehicle.vehicle_model_id"
                          :options="models"
                          id="vehicle_model_id"
                          :reduce="(val) => val.id"
                          label="name"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="entity_group_id"
                    >
                      <b-form-group
                        :label="$t('entity group') | capitalize"
                        label-for="entity_group_id"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="vehicle.entity_group_id"
                          :options="entityGroups"
                          id="entity_group_id"
                          :reduce="(val) => val.value"
                          label="text"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="vehicle_supplier_id"
                    >
                      <b-form-group
                        :label="$t('Supplier') | capitalize"
                        label-for="vehicle_supplier_id"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="vehicle.vehicle_supplier_id"
                          :options="suppliers"
                          id="vehicle_supplier_id"
                          :reduce="(val) => val.value"
                          label="name"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                </b-row>
              </template>
            </b-card>
          </template>

          <template>
            <b-card>
              <template #header>
                <b-card-title class="form-title">{{
                  $t("Contract Information")
                }}</b-card-title>
              </template>
              <template #default>
                <b-row>
                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="contract_type"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('Contract Type') | capitalize"
                        label-for="contract_type"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="vehicle.contract_type"
                          :options="contractTypes"
                          id="contract_type"
                          :reduce="(val) => val.id"
                          label="name"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="license_plate"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('License Plate') | capitalize"
                        label-for="license_plate"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="license_plate"
                          v-model="vehicle.license_plate"
                          name="license_plate"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="fabrication_year"
                    >
                      <b-form-group
                        :label="$t('Fabrication Year') | capitalize"
                        label-for="fabrication_year"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="vehicle.fabrication_year"
                          :options="fabricationYears"
                          id="fabrication_year"
                          :reduce="(val) => val.value"
                          label="name"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider v-slot="{ errors }" name="model_year">
                      <b-form-group
                        :label="$t('Model Year') | capitalize"
                        label-for="model_year"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="vehicle.model_year"
                          :options="modelYears"
                          id="model_year"
                          :reduce="(val) => val.value"
                          label="name"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider v-slot="{ errors }" name="model">
                      <b-form-group
                        :label="$t('Model') | capitalize"
                        label-for="model"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="model"
                          v-model="vehicle.model"
                          name="model"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider v-slot="{ errors }" name="current_km">
                      <b-form-group
                        :label="$t('Current KM') | capitalize"
                        label-for="current_km"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="current_km"
                          v-model="vehicle.current_km"
                          name="current_km"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                </b-row>
              </template>
            </b-card>
          </template>

          <template>
            <b-card>
              <template #header>
                <b-card-title class="form-title">{{
                  $t("Capacity and Specifications")
                }}</b-card-title>
              </template>
              <template #default>
                <b-row>
                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="load_capacity_type"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('Load Capacity Type') | capitalize"
                        label-for="load_capacity_type"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="vehicle.load_capacity_type"
                          :options="loadCapactityTypes"
                          id="load_capacity_type"
                          :reduce="(val) => val.id"
                          label="name"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="load_capacity"
                    >
                      <b-form-group
                        :label="$t('Load Capacity') | capitalize"
                        label-for="load_capacity"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="load_capacity"
                          v-model="vehicle.load_capacity"
                          name="load_capacity"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="gallons_capacity"
                    >
                      <b-form-group
                        :label="$t('Gallons Capacity') | capitalize"
                        label-for="gallons_capacity"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="gallons_capacity"
                          v-model="vehicle.gallons_capacity"
                          name="gallons_capacity"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="motor_number"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('Motor Number') | capitalize"
                        label-for="motor_number"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="motor_number"
                          v-model="vehicle.motor_number"
                          name="motor_number"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="chassis_number"
                    >
                      <b-form-group
                        :label="$t('Chassis Number') | capitalize"
                        label-for="chassis_number"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="chassis_number"
                          v-model="vehicle.chassis_number"
                          name="chassis_number"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider v-slot="{ errors }" name="color">
                      <b-form-group
                        :label="$t('Color') | capitalize"
                        label-for="color"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="color"
                          v-model="vehicle.color"
                          name="color"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="wheels_number"
                    >
                      <b-form-group
                        :label="$t('Wheels Number') | capitalize"
                        label-for="wheels_number"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="wheels_number"
                          v-model="vehicle.wheels_number"
                          name="wheels_number"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="cilinders_number"
                    >
                      <b-form-group
                        :label="$t('Cilinders Number') | capitalize"
                        label-for="cilinders_number"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="cilinders_number"
                          v-model="vehicle.cilinders_number"
                          name="cilinders_number"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="seatings_number"
                    >
                      <b-form-group
                        :label="$t('Seatings Number') | capitalize"
                        label-for="seatings_number"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="seatings_number"
                          v-model="vehicle.seatings_number"
                          name="seatings_number"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="passengers_number"
                    >
                      <b-form-group
                        :label="$t('Passengers Number') | capitalize"
                        label-for="passengers_number"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="passengers_number"
                          v-model="vehicle.passengers_number"
                          name="passengers_number"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider v-slot="{ errors }" name="axies_number">
                      <b-form-group
                        :label="$t('Axies Number') | capitalize"
                        label-for="axies_number"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="axies_number"
                          v-model="vehicle.axies_number"
                          name="axies_number"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider v-slot="{ errors }" name="mtc_number">
                      <b-form-group
                        :label="$t('MTC Number') | capitalize"
                        label-for="mtc_number"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="mtc_number"
                          v-model="vehicle.mtc_number"
                          name="mtc_number"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                </b-row>
              </template>
            </b-card>
          </template>

          <template>
            <b-card>
              <template #header>
                <b-card-title class="form-title">{{
                  $t("Weight and Dimensions")
                }}</b-card-title>
              </template>
              <template #default>
                <b-row>
                  <b-col md="3">
                    <ValidationProvider v-slot="{ errors }" name="dry_weight">
                      <b-form-group
                        :label="$t('Dry Weight') | capitalize"
                        label-for="dry_weight"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="dry_weight"
                          v-model="vehicle.dry_weight"
                          name="dry_weight"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider v-slot="{ errors }" name="gross_weight">
                      <b-form-group
                        :label="$t('Gross Weight') | capitalize"
                        label-for="gross_weight"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="gross_weight"
                          v-model="vehicle.gross_weight"
                          name="gross_weight"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider v-slot="{ errors }" name="useful_load">
                      <b-form-group
                        :label="$t('Useful Load') | capitalize"
                        label-for="useful_load"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="useful_load"
                          v-model="vehicle.useful_load"
                          name="useful_load"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider v-slot="{ errors }" name="hp_motor">
                      <b-form-group
                        :label="$t('Horsepower Motor') | capitalize"
                        label-for="hp_motor"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="hp_motor"
                          v-model="vehicle.hp_motor"
                          name="hp_motor"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider v-slot="{ errors }" name="length">
                      <b-form-group
                        :label="$t('Length') | capitalize"
                        label-for="length"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="length"
                          v-model="vehicle.length"
                          name="length"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider v-slot="{ errors }" name="width">
                      <b-form-group
                        :label="$t('Width') | capitalize"
                        label-for="width"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="width"
                          v-model="vehicle.width"
                          name="width"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider v-slot="{ errors }" name="height">
                      <b-form-group
                        :label="$t('Height') | capitalize"
                        label-for="height"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="height"
                          v-model="vehicle.height"
                          name="height"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="pinias_number"
                    >
                      <b-form-group
                        :label="$t('Pinias Number') | capitalize"
                        label-for="pinias_number"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="pinias_number"
                          v-model="vehicle.pinias_number"
                          name="pinias_number"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                </b-row>
              </template>
            </b-card>
          </template>

          <template>
            <b-card>
              <template #header>
                <b-card-title class="form-title">{{
                  $t("Additional Information")
                }}</b-card-title>
              </template>
              <template #default>
                <b-row>
                  <b-col md="3">
                    <ValidationProvider v-slot="{ errors }" name="fuel">
                      <b-form-group
                        :label="$t('Fuel') | capitalize"
                        label-for="fuel"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="fuel"
                          v-model="vehicle.fuel"
                          name="fuel"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                  <b-col md="3">
                    <ValidationProvider v-slot="{ errors }" name="bodywork">
                      <b-form-group
                        :label="$t('Bodywork') | capitalize"
                        label-for="bodywork"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="bodywork"
                          v-model="vehicle.bodywork"
                          name="bodywork"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                  <b-col md="3">
                    <ValidationProvider v-slot="{ errors }" name="guide_number">
                      <b-form-group
                        :label="$t('Guide Number') | capitalize"
                        label-for="guide_number"
                        :state="errors[0] ? false : null"
                      >
                        <b-form-input
                          v-if="finishedLoading"
                          id="guide_number"
                          v-model="vehicle.guide_number"
                          name="guide_number"
                          :state="errors[0] ? false : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="semitrailer_id"
                    >
                      <b-form-group
                        :label="$t('Semitrailer') | capitalize"
                        label-for="semitrailer_id"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="vehicle.semitrailer_id"
                          :options="semiTrailers"
                          id="semitrailer_id"
                          :reduce="(val) => val.value"
                          label="name"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                </b-row>
              </template>
            </b-card>
          </template>
        </b-form>
      </ValidationObserver>
    </template>

    <template #footer>
      <b-row class="px-3 py-2">
        <b-col lg="6" v-if="!vehicleId">
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            type="button"
            variant="outline-info"
            @click="resetForm"
            >{{ $t("RESET") }}
          </b-button>
        </b-col>
        <b-col lg="6" :offset="vehicleId ? 6 : 0">
          <div class="d-flex justify-content-end">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="outline-primary"
              class="mr-2"
              @click="createOrUpdateVehicle"
            >
              <span> {{ vehicleId == null ? $t("SAVE") : $t("UPDATE") }}</span>
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-danger"
              @click="closeSidebar(false)"
              >{{ $t("CANCEL") }}
            </b-button>
          </div>
        </b-col>
      </b-row>
    </template>
  </b-sidebar>
</template>

<script>
import { mapGetters } from "vuex";
import Ripple from "vue-ripple-directive";
import EmployeeService from "@/views/rrhh/views/employees/services/employee.service.js";
import VehicleService from "@/views/commons/vehicles/services/vehicle.service.js";

export default {
  directives: {
    Ripple,
  },
  props: {
    vehicleId: {
      type: Number,
      required: false,
    },
  },
  data() {
    return {
      showSidebar: false,
      vehicle: {
        vehicle_brand_id: "",
        setting_vehicle_id: "",
        vehicle_type_id: "",
        vehicle_model_id: "",
        entity_group_id: "",
        vehicle_supplier_id: "",
        contract_type: "",
        license_plate: "",
        fabrication_year: "",
        model_year: "",
        model: "",
        current_km: "",
        load_capacity_type: "",
        load_capacity: "",
        gallons_capacity: "",
        motor_number: "",
        chassis_number: "",
        color: "",
        wheels_number: "",
        cilinders_number: "",
        seatings_number: "",
        passengers_number: "",
        axies_number: "",
        mtc_number: "",
        dry_weight: "",
        gross_weight: "",
        useful_load: "",
        hp_motor: "",
        length: "",
        width: "",
        height: "",
        pinias_number: "",
        fuel: "",
        bodywork: "",
        guide_number: "",
        semitrailer_id: "",
      },

      finishedLoading: false,
      disableInput: false,

      brands: [],
      settings: [],
      types: [],
      models: [],
      entityGroups: [],
      suppliers: [],
      contractTypes: [],
      fabricationYears: [],
      modelYears: [],
      loadCapactityTypes: [],
      semiTrailers: [],
    };
  },
  async mounted() {
    this.addPreloader();
    this.showSidebar = true;
    await Promise.all([
      this.listVehicleBrands(),
      this.listVehicleSettings(),
      this.listVehicleTypes(),
      this.listVehicleModel(),
      this.getEntityGroups(),
      this.listVehicleSuppliers(),
      this.listVehicleContractTypes(),
      this.listVehicleLoadCapacity(),
      this.fillYears(),
    ]);
    if (this.vehicleId) {
      this.getVehicleById();
    }
    this.finishedLoading = true;
    this.removePreloader();
  },
  computed: {
    ...mapGetters({
      currentUser: "auth/currentUser",
    }),
  },
  methods: {
    async getVehicleById() {
      try {
        const { data } = await VehicleService.getVehicleById({
          id: this.vehicleId,
        });
        this.vehicle = data;
      } catch (error) {
        console.log(error);
      }
    },
    async getEntityGroups() {
      try {
        const { data } = await EmployeeService.getEntitiesGroup();
        this.entityGroups = data;
      } catch (error) {
        console.log(error);
      }
    },
    closeSidebar(action) {
      this.$emit("hideSidebar", action);
    },
    resetForm() {
      this.vehicle = {};
    },
    async createOrUpdateVehicle() {
      try {
        const result = await this.$refs["vehicle-form"].validate();
        if (!result) return;
        const confirm = await this.showConfirmSwal();
        if (!confirm.isConfirmed) return;

        this.addPreloader();

        const { data } = await VehicleService.createOrUpdateVehicle({
          id: this.vehicleId,
          vehicle: this.vehicle,
        });

        this.showToastSuccess(data.message);
        this.$emit(this.vehicleId ? "updateVehicle" : "createVehicle");
      } catch (e) {
        this.showToastError(e);
      } finally {
        this.removePreloader();
      }
    },
    async listVehicleBrands() {
      try {
        const { data } = await VehicleService.listVehicleBrands();
        this.brands = data;
      } catch (error) {
        console.log(error);
      }
    },
    async listVehicleSettings() {
      try {
        const { data } = await VehicleService.listVehicleSettings();
        this.settings = data;
      } catch (error) {
        console.log(error);
      }
    },
    async listVehicleTypes() {
      try {
        const { data } = await VehicleService.listVehicleTypes();
        this.types = data;
      } catch (error) {
        console.log(error);
      }
    },
    async listVehicleModel() {
      try {
        const { data } = await VehicleService.listVehicleModel();
        this.models = data;
      } catch (error) {
        console.log(error);
      }
    },
    async listVehicleSuppliers() {
      try {
        const { data } = await VehicleService.listVehicleSuppliers();
        this.suppliers = data;
      } catch (error) {
        console.log(error);
      }
    },
    async listVehicleContractTypes() {
      try {
        const { data } = await VehicleService.listVehicleContractTypes();
        this.contractTypes = data;
      } catch (error) {
        console.log(error);
      }
    },
    async listVehicleLoadCapacity() {
      try {
        const { data } = await VehicleService.listVehicleLoadCapacity();
        this.loadCapactityTypes = data;
      } catch (error) {
        console.log(error);
      }
    },
    fillYears() {
      let init_year = 1950;
      let end_year = new Date().getFullYear();
      let array_of_year = [];
      for (let index = init_year; index <= end_year; index++) {
        array_of_year.push({ value: index, name: index });
      }
      this.modelYears = array_of_year;
      this.fabricationYears = array_of_year;
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

// .v-select.drop-up.vs--open .vs__dropdown-toggle {
//   border-radius: 0 0 4px 4px;
//   border-top-color: transparent;
//   border-bottom-color: rgba(60, 60, 60, 0.26);
// }

// [data-popper-placement="top"] {
//   border-radius: 4px 4px 0 0;
//   border-top-style: solid;
//   border-bottom-style: none;
//   box-shadow: 0 -3px 6px rgba(0, 0, 0, 0.15);
// }

// #overpx .vs__dropdown-toggle .vs__selected-options .vs__search {
//   margin: 9.15px 0 0 !important;
// }

.sidebar-xl {
  width: 90rem !important;
}

.b-sidebar-header {
  flex-direction: row-reverse !important;
  justify-content: space-between !important;

  .close {
    margin-right: revert !important;
  }
}

.form-title {
  font-size: 1.25rem !important;
  font-weight: 600 !important;
  color: #212529 !important;
}

.dark-layout .form-title {
  color: #fff !important;
}
</style>